import React, { useState, MouseEvent, useMemo, useEffect, useRef } from 'react';
import styles from './lessonSection.module.scss';
import RightIcon from '../../../../assets/icons/chewron_right.svg?react';
import ChevronDown from '@src/assets/icons/chevron_down.svg?react';
import { Content, Lesson } from '../../../../types';
import { useCourseContent } from '../../../../hooks/useCourseContent';
import { ContentType, LastVisitedLessonContentFragment as LastVisitedLesson } from 'src/api/graphql-codegen';
import SectionContents from './section-contents/SectionContents';
import { useAnalytics, Events, useUser } from '@hooks';

type Props = {
  title: string;
  contents: Content[];
  lesson: Lesson;
  lastVisitedLesson?: LastVisitedLesson;
  trackEventName?: string;
  closeNav?: () => void;
};

function LessonSection(props: Props) {
  const { title, contents = [], lesson, lastVisitedLesson, trackEventName, closeNav } = props;

  const { trackEvent } = useAnalytics();
  const [user] = useUser();

  const [opened, setOpened] = useState(false);
  const progressRef = useRef<HTMLDivElement>(null);
  const [progressWidth, setProgressWidth] = useState(0);

  const {
    content: selectedContent,
    lesson: selectedLesson,
    currentUnit,
    program,
    section,
    startedAlternateLesson,
    isParentApp,
    isNewLessonLayout,
    isStudyGuide,
    isStudentQuiz,
    studentQuiz,
    setLastContent,
  } = useCourseContent();

  const handleToggle = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const _opened = !opened;
    setOpened(_opened);
    if (_opened && !isParentApp) {
      const _d = {
        title,
        ...Events.mappers.program(program),
        ...Events.mappers.lesson(selectedLesson),
        schoolId: user?.schools?.[0]?.id,
        schoolName: user?.schools?.[0]?.name,
        userId: user?.id,
        userName: `${user?.firstName} ${user?.lastName}`,
      };
      trackEvent(trackEventName || '', _d);
    }
  };

  useEffect(() => {
    if (lastVisitedLesson && lastVisitedLesson?.lessonId === lesson.id) {
      setOpened(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastVisitedLesson]);

  useEffect(() => {
    if (selectedContent && contents.map(c => c.id).includes(selectedContent.id)) {
      setOpened(true);
    }
  }, [selectedLesson, lesson, selectedContent, contents]);

  const wrapperClassName = useMemo(() => {
    const classes = [styles.wrapper];
    if (opened) {
      classes.push(styles.opened);
    }

    if (contents.find(c => c.id === selectedContent?.id) && selectedLesson?.id === lesson.id) {
      classes.push(styles.selected);
    }

    return classes.join(' ');
  }, [opened, selectedContent, contents, lesson, selectedLesson]);

  const modules = useMemo(() => {
    // StudyGuide Modules
    if (isStudyGuide && currentUnit?.studyGuides) {
      return currentUnit.studyGuides
    }
    // Quiz Modules
    if (isStudentQuiz && studentQuiz?.contents) {
       const normalModules = studentQuiz?.contents.filter(content => content.type !== ContentType.FORM)
       const quizModule = studentQuiz?.contents.filter(content => content.type === ContentType.FORM)
       const quizModules = [...normalModules, quizModule[0]]
       return quizModules
    }
    // Alternate Lesson
    if (section && section.modules?.find(item => item?.id === selectedContent?.id)) {
      return section.modules
    }
    // Main Lesson
    if (lesson && lesson.modules?.find(item => item?.id === selectedContent?.id)) {
      const preExitTicket = lesson.modules.find(module => module.name.toLowerCase().includes("exit ticket") && module.type === ContentType.SLIDE)
      const exitTicket = lesson.modules.find(module => module.name.toLowerCase().includes("exit ticket") && module.type === ContentType.FORM)
      if(preExitTicket && exitTicket) {
        return lesson.modules.filter(module => module.lastInLesson !== true && module !== preExitTicket); // Remove "Lesson Complete" and "PreExitTicket" module on Progress
      }
      return lesson.modules.filter(module => module.lastInLesson !== true); // Remove Lesson Complete module on Progress
    } else {
      return []
    }
  }, [selectedContent, lesson, section, isStudyGuide, currentUnit, isStudentQuiz, studentQuiz])

  const moduleIndex = useMemo(() => {
    // StudyGuide Modules
    if (isStudyGuide && currentUnit?.studyGuides) {
      return currentUnit.studyGuides.findIndex(m => m?.id === selectedContent?.id);
    }
    // Quiz Modules
    if (isStudentQuiz && studentQuiz?.contents) {
      return studentQuiz?.contents.findIndex(m => m?.id === selectedContent?.id);
    }
    // Alternate Lesson
    if (section && section.modules?.find(item => item?.id === selectedContent?.id)) {
      return section.modules.findIndex(m => m?.id === selectedContent?.id);
    }
    // Main Lesson
    if (lesson && lesson.modules?.find(item => item?.id === selectedContent?.id)) {
      const progressModules = lesson.modules.filter(module => module.lastInLesson !== true);
      const lessonCompleteModule = lesson.modules.find(module => module.lastInLesson === true);
      if (lessonCompleteModule && lessonCompleteModule.id === selectedContent?.id) {
        return lesson.modules.findIndex(module => module === lessonCompleteModule);
      }
      return progressModules.findIndex(m => m?.id === selectedContent?.id);
    }
    return -1
  }, [selectedContent, lesson, section, isStudyGuide, currentUnit, isStudentQuiz, studentQuiz])

  useEffect(() => {
    // Alternate Lesson
    if (startedAlternateLesson && section && section.modules?.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var contentIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if(contentIndex > 0) {
        if(contentIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * contentIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    } // Main Lesson
    else if (lesson && lesson.modules?.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var lessonmodules = lesson.modules.filter(module => module.lastInLesson !== true);
      const preExitTicket = lessonmodules.find(module => module.name.toLowerCase().includes("exit ticket") && module.type === ContentType.SLIDE)
      const exitTicket = lessonmodules.find(module => module.name.toLowerCase().includes("exit ticket") && module.type === ContentType.FORM)
      const lessonCompleteModule = lesson.modules.find(module => module.lastInLesson === true);

      var moduleIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if (preExitTicket && exitTicket && preExitTicket.id === selectedContent?.id) {
        moduleIndex = modules.findIndex(m => m?.id === exitTicket.id)
      } else if (lessonCompleteModule && lessonCompleteModule.id === selectedContent?.id) {
        moduleIndex = lesson.modules.findIndex(m => m.id === lessonCompleteModule.id)
      }
      if(moduleIndex > 0) {
        if(moduleIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * moduleIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    } // Study Guide
    else if (isStudyGuide && currentUnit?.studyGuides && currentUnit?.studyGuides.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var studyIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if(studyIndex > 0) {
        if(studyIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * studyIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    } // Quiz Progress 
    else if (isStudentQuiz && studentQuiz?.contents && studentQuiz?.contents.find(item => item?.id === selectedContent?.id) && progressRef.current) {
      var quizIndex = modules.findIndex(m => m?.id === selectedContent?.id);
      if(quizIndex > 0) {
        if(quizIndex < modules.length - 1) {
          setProgressWidth((progressRef.current.clientWidth) * quizIndex / (modules.length - 1));
          setLastContent(false);
        } else {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        }
      } else {
        if (modules.length === 1) {
          setProgressWidth(progressRef.current.clientWidth);
          setLastContent(true);
        } else {
          setProgressWidth(0)
          setLastContent(false);
        }
      }
    }
  },[selectedContent, startedAlternateLesson, lesson, section, modules, isStudyGuide, currentUnit, isStudentQuiz, studentQuiz, setLastContent])

  return (
    <div className={wrapperClassName}>
      <div onClick={handleToggle} className={styles.header}>
        <RightIcon className={styles.rightIcon}/>
        <div className={isNewLessonLayout ? styles.contentTitle : styles.title}>{isNewLessonLayout ? 'Lesson Content:' : title}</div>
        <div className={[styles.dropdownToggle, opened ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
          <ChevronDown className={styles.icon}/>
        </div>
      </div>
      {isNewLessonLayout ? 
      <div ref={progressRef} className={styles.progressBackBar}>
        <div className={styles.progressBar} style={{width: `${progressWidth}px` }}>
        </div>
      </div> : null}
      <div className={styles.content}>
        <SectionContents contents={contents} lesson={lesson} lastVisitedLesson={lastVisitedLesson} closeNav={closeNav} selectedModuleIndex={moduleIndex}/>
      </div>
    </div>
  );
}

export default React.memo(LessonSection);
